import Layout from "@components/layout/layout";
import NewsStackHeader from "@components/news-stack/news-stack-header";
import ExploreTopics from "@components/core/explore-topics";
import StickyBox from "react-sticky-box";
import NewsSkeletonLoading from "./news-skeleton-loading";
import dynamic from "next/dynamic";
import SideBar from "@components/sidebar/sidebar";
import { useRouter } from "next/router";
import { useTopNewsStackListQuery } from "@graphql/generated/graphql";
import "react-loading-skeleton/dist/skeleton.css";
import { useMainBannerContext } from "@lib/mainBannerContext";
import SideBarTrendingKeywords from "@components/sidebar/side-bar-trending-keywords";

const NewsStackPreviewCards = dynamic(
  () => import("@components/news-stack/news-stack-preview-cards")
);
const NewsList = dynamic(() => import("./news-list"));

interface Topic {
  name: JSX.Element | string;
  key: string;
  emoji?: string;
  bgActiveClass?: string;
  seoTitle: string;
  seoDescription: string;
}

interface TopicsProps {
  topics: Topic[];
}

const NewsLandingPage: React.FunctionComponent<TopicsProps> = ({ topics }) => {
  const { locale, query } = useRouter();
  const topic = query.topic;
  const showMainBanner = useMainBannerContext();

  const { data, loading, error } = useTopNewsStackListQuery({
    variables: {
      languageCode: locale,
    },
  });

  return (
    <Layout
      showNewsStacks={true}
      showCTA={false}
      showFooter={false}
      className="!overflow-visible"
      retainWholePage={true}
    >
      <div className="mx-auto my-6 flex max-w-7xl flex-row items-start px-4 2xs:px-6 lg:space-x-20">
        <section className="w-full flex-1 md:mt-5 lg:w-1/2">
          {loading || error ? (
            <NewsSkeletonLoading />
          ) : data.allCategories[0].news.length > 0 ? (
            <>
              <div className="mb-8">
                <NewsStackHeader newsStack={data.allCategories[0]} />
              </div>
              <div className="mb-8">
                <NewsStackPreviewCards newsStack={data.allCategories[0]} />
              </div>
            </>
          ) : (
            <div className="mb-8">
              <div className="md:flex md:space-x-3 md:pt-0">
                <div className="mb-8 md:w-1/2">
                  <NewsStackHeader newsStack={data.allCategories[0]} />
                </div>
                <NewsStackPreviewCards newsStack={data.allCategories[0]} />
              </div>
            </div>
          )}
          <div
            id="explore-topic-scroll-target"
            className={showMainBanner ? "h-10" : "h-0"}
          ></div>
          <div>
            {/* Added an abitrary div to scroll to view when ExploreTopics is in sticky position */}
            <div
              className={`sticky ${
                showMainBanner ? "top-14" : "top-0"
              } z-10 bg-white`}
            >
              <ExploreTopics topics={topics} />
            </div>

            {topic === "Trending" && (
              <div className="mb-4">
                <SideBarTrendingKeywords />
              </div>
            )}

            <div className="mb-8">
              <NewsList />
            </div>
          </div>
        </section>

        <StickyBox offsetTop={20} offsetBottom={20}>
          <SideBar
            showTrending={false}
            showTrendingKeywords={topic !== "Trending"}
          />
        </StickyBox>
      </div>
    </Layout>
  );
};

export default NewsLandingPage;
